/**
 * 업무 프로젝트 자동화 — 노드 설정 패널 부품(J7d · 서버 호출 없음 · 자동화 탭 조립은 J7c2).
 *
 * - 잔디 실측: 캔버스 오른쪽 폭 260 · 노드를 안 골랐으면 안내 한 줄 · 패널에 노드 삭제 버튼이 없다
 *   (지우기는 캔버스 Delete — J7c1). 색·모양은 uiStyles.
 * - 노드 data 는 부모가 든다 — 이 부품은 onChange(그 노드의 «전체» data)로만 알린다. 바뀐 키만 넘기면
 *   J7c2 가 data 를 통째로 갈아 끼우면서 나머지 키가 사라진다.
 * - 🔴 select 의 e.target.value 는 늘 문자열이다 — 선택지·멤버 id 는 Number, «없음»·«첫 상태»('')는 null,
 *   «트리거한 사람»·«지금 담당자»는 `{{…}}` 문자열 그대로 싣는다(J7a1 은 "11"·true 를 거절한다).
 *   채널 id 만 형변환 없이 문자열 그대로다.
 * - 화면에서 미리 막는 것은 같은 필드 중복·줄 개수 상한뿐 — 빈 제목·빈 메시지 등은 저장 422 로 보인다(J7c2).
 * - 🔴 입력칸의 Delete·Backspace 는 캔버스(J7c1 onKeyDown)가 무시한다 — 패널은 따로 막지 않는다.
 */
import { useRef } from 'react';
import { COLORS, RADIUS, btn, input as inputBase } from '../../../utils/uiStyles';
import { ASSIGNEE_MODES, FIELDS, FIELD_LABELS, GROUPS, nodeSpec } from './automationCatalog';

// J7a1 `backend/services/task_pm_automation.py` 와 같은 값.
const ACTOR = '{{actor.id}}';                   // 규칙을 일으킨 사람
const CURRENT_ASSIGNEE = '{{task.assignee}}';   // 대상 업무의 지금 담당자 전원(업무 만들기에서만)
const MAX_CONDITIONS = 10;
const MAX_SET_FIELDS = 4;
const MAX_TITLE_LEN = 300;
const MAX_MESSAGE_LEN = 2000;
// ⑧·⑨ — 지워지거나 보관된 업무 아래에는 하위 업무를 만들 수 없다.
const SUBTASK_BLOCKED = ['trigger_task_deleted', 'trigger_task_archived'];

const OPERATORS = [
  { value: 'eq', label: '같음' },
  { value: 'neq', label: '다름' },
  { value: 'empty', label: '비어 있음' },
  { value: 'not_empty', label: '값 있음' },
];
// 담당자는 여러 명이다(J1) — 서버가 eq/neq 를 «목록에 있다/없다» 로 본다 → 담당자 줄만 이름을 바꾼다(저장 값은 그대로).
const ASSIGNEE_OPERATORS = [
  { value: 'eq', label: '포함' },
  { value: 'neq', label: '포함 안 함' },
  { value: 'empty', label: '비어 있음' },
  { value: 'not_empty', label: '비어 있지 않음' },
];
const COMPARE = ['eq', 'neq'];   // 비교값이 있는 연산자

// 변수 넣기 버튼 — 🔴 name 은 J7a1 VARIABLES 와 글자·순서까지 같다. trigger = 그 변수를 쓸 수 있는 트리거(공통은 null).
export const VARIABLE_DEFS = [
  { name: 'task.key', label: '업무 번호', trigger: null },
  { name: 'task.title', label: '업무 제목', trigger: null },
  { name: 'task.url', label: '업무 링크', trigger: null },
  { name: 'project.name', label: '프로젝트 이름', trigger: null },
  { name: 'actor.name', label: '트리거한 사람 이름', trigger: null },
  { name: 'change.field', label: '바뀐 필드', trigger: 'trigger_field_change' },
  { name: 'change.before', label: '바뀌기 전', trigger: 'trigger_field_change' },
  { name: 'change.after', label: '바뀐 뒤', trigger: 'trigger_field_change' },
  { name: 'comment.body', label: '댓글 내용', trigger: 'trigger_comment_added' },
];

const NONE = { value: '', label: '없음' };
const PICK = { value: '', label: '값 고르기' };   // 값이 비어 있으면 안 되는 칸이 비어 있을 때만 보인다
const ACTOR_ITEM = { value: ACTOR, label: '트리거한 사람' };
const CURRENT_ITEM = { value: CURRENT_ASSIGNEE, label: '지금 담당자(전원)' };

const labelText = { display: 'block', fontSize: '12px', color: COLORS.textSub, marginBottom: '4px' };
const block = { display: 'block', marginBottom: '12px' };
const control = { ...inputBase, padding: '6px 8px', fontSize: '13px' };
const note = { fontSize: '12px', color: COLORS.textSub };
const rowBox = {
  display: 'flex', flexDirection: 'column', gap: '6px', padding: '8px', marginBottom: '8px',
  border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.md,
};
const rowHead = { display: 'flex', alignItems: 'center', justifyContent: 'space-between' };
const checkRow = { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: COLORS.text, marginBottom: '4px' };
const smallButton = (variant, disabled, extra) => ({
  ...btn(variant, 'sm'), ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : null), ...extra,
});
const removeButton = (disabled) => ({
  border: 'none', background: 'none', padding: '0 4px', fontSize: '13px', lineHeight: 1,
  color: COLORS.textSub, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
});

// 화면 값(문자열) ↔ data 값
const encode = (value) => (value == null ? '' : String(value));
const decode = (text) => {
  if (text === '') return null;
  if (text === ACTOR || text === CURRENT_ASSIGNEE) return text;
  const n = Number(text);
  return Number.isFinite(n) ? n : text;
};

// 그 kind 의 선택지를 sort_order 순으로
const sortedOptions = (options, kind) => {
  const list = options && Array.isArray(options[kind]) ? options[kind] : [];
  return list
    .filter((o) => o && o.id != null)
    .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0));
};

// 지금 값이 숫자인데 목록에 없으면(지워진 선택지·나간 멤버) 항목을 하나 더 둬서 값이 바뀌지 않게 한다.
const withMissing = (items, value, field) => {
  if (typeof value !== 'number' || items.some((it) => it.value === String(value))) return items;
  return [...items, { value: String(value), label: field === 'assignee' ? '(없는 멤버)' : '(없는 선택지)' }];
};

export default function AutomationNodePanel({
  node,
  triggerType = null,
  options,
  members,
  channels,
  channelsEnabled,
  readOnly = false,
  onChange,
}) {
  const textRef = useRef(null);   // 변수 넣기 대상 — 제목 또는 메시지(노드 하나에 하나뿐)

  const spec = node ? nodeSpec(node.type) : null;
  const group = spec ? GROUPS.find((g) => g.value === spec.group) : null;
  const data = node && node.data && typeof node.data === 'object' && !Array.isArray(node.data) ? node.data : {};
  const emit = (patch) => { if (!readOnly && onChange) onChange({ ...data, ...patch }); };

  // 멤버는 거르지 않는다(비활성은 저장 422 로 드러난다) — 표시는 name || email.
  const memberList = Array.isArray(members) ? members.filter((m) => m && m.user_id != null) : [];
  const memberItems = memberList.map((m) => ({ value: String(m.user_id), label: m.name || m.email || String(m.user_id) }));
  const optionItems = (kind) => sortedOptions(options, kind).map((o) => ({ value: String(o.id), label: o.label }));
  // 그 필드의 값 후보(화면 차례) — «안 쓴 첫 값»·«첫 값» 이 이 차례를 따른다.
  const candidates = (field) => (field === 'assignee'
    ? [ACTOR, ...memberList.map((m) => Number(m.user_id))]
    : sortedOptions(options, field).map((o) => Number(o.id)));

  const pickSelect = ({ ariaLabel, value, items, onPick }) => (
    <select aria-label={ariaLabel} value={encode(value)} disabled={readOnly}
      onChange={(e) => onPick(decode(e.target.value))} style={control}>
      {items.map((it) => (
        <option key={it.value} value={it.value} disabled={Boolean(it.disabled)}>{it.label}</option>
      ))}
    </select>
  );
  const labeled = (text, child) => (
    <label style={block}>
      <span style={labelText}>{text}</span>
      {child}
    </label>
  );

  // ─── 변수 넣기 ──────────────────────────────────────────
  const insertVariable = (key, name) => {
    if (readOnly) return;
    const text = typeof data[key] === 'string' ? data[key] : '';
    const el = textRef.current;
    const start = el && typeof el.selectionStart === 'number' ? Math.min(Math.max(el.selectionStart, 0), text.length) : text.length;
    const end = el && typeof el.selectionEnd === 'number' ? Math.min(Math.max(el.selectionEnd, start), text.length) : start;
    emit({ [key]: `${text.slice(0, start)}{{${name}}}${text.slice(end)}` });
    if (el) el.focus();
  };
  const renderVariables = (key) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', margin: '-6px 0 12px' }}>
      {VARIABLE_DEFS.filter((v) => v.trigger === null || v.trigger === triggerType).map((v) => (
        <button key={v.name} type="button" title={`{{${v.name}}}`} disabled={readOnly}
          onClick={() => insertVariable(key, v.name)}
          style={smallButton('secondary', readOnly, { padding: '2px 6px', fontSize: '11px' })}>
          {v.label}
        </button>
      ))}
    </div>
  );

  // ─── 필드 변경 트리거 ───────────────────────────────────
  const renderFieldChange = () => {
    // 배열이 아니면 빈 목록으로 본다(카탈로그 summarize 와 같은 해석).
    const chosen = Array.isArray(data.fields) ? FIELDS.filter((f) => data.fields.includes(f)) : [];
    const toggle = (field, on) => {
      const next = on ? [...chosen, field] : chosen.filter((f) => f !== field);
      if (!next.length) return;   // 마지막 하나는 끌 수 없다
      emit({ fields: FIELDS.filter((f) => next.includes(f)) });
    };
    return (
      <div role="group" aria-label="살필 필드">
        <div style={labelText}>살필 필드</div>
        {FIELDS.map((field) => {
          const checked = chosen.includes(field);
          return (
            <label key={field} style={checkRow}>
              <input type="checkbox" checked={checked} disabled={readOnly || (checked && chosen.length === 1)}
                onChange={(e) => toggle(field, e.target.checked)} />
              {FIELD_LABELS[field]}
            </label>
          );
        })}
      </div>
    );
  };

  // ─── 조건 분기 ──────────────────────────────────────────
  const renderCondition = () => {
    const rows = Array.isArray(data.conditions)
      ? data.conditions.map((r) => (r && typeof r === 'object' ? r : {}))
      : [];
    const logic = data.logic === 'or' ? 'or' : 'and';
    const many = rows.length >= 2;
    // 같은 필드의 다른 줄이 이미 고른 값
    const usedBy = (field, except) => rows
      .filter((r, i) => i !== except && r.field === field && COMPARE.includes(r.operator) && r.value != null)
      .map((r) => r.value);
    const freeValue = (field, except) => {
      const used = usedBy(field, except);
      const free = candidates(field).find((v) => !used.includes(v));
      return free === undefined ? null : free;
    };
    // 다른 줄이 안 쓴 첫 값 · 다 썼으면 첫 값
    const firstValue = (field, except) => {
      const free = freeValue(field, except);
      if (free !== null) return free;
      const all = candidates(field);
      return all.length ? all[0] : null;
    };
    const update = (index, patch) => emit({ conditions: rows.map((r, i) => (i === index ? { ...r, ...patch } : r)) });
    // 🔴 필드를 바꾸면 값도 다시 잡는다 — 엉뚱한 kind 의 id 가 남지 않게.
    const changeField = (index, field) => update(index, {
      field,
      value: COMPARE.includes(rows[index].operator) ? firstValue(field, index) : null,
    });
    const changeOperator = (index, operator) => {
      const current = rows[index].value;
      let value = current === undefined ? null : current;
      if (!COMPARE.includes(operator)) value = null;
      else if (value == null) value = firstValue(rows[index].field, index);
      update(index, { operator, value });
    };
    const full = rows.length >= MAX_CONDITIONS;
    const add = () => {
      if (full) return;
      emit({ conditions: [...rows, { field: 'status', operator: 'eq', value: freeValue('status', -1) }] });
    };
    const remove = (index) => {
      if (!many) return;
      emit({ conditions: rows.filter((_, i) => i !== index) });
    };
    const valueItems = (row, index) => {
      const used = usedBy(row.field, index);
      const base = row.field === 'assignee' ? [ACTOR_ITEM, ...memberItems] : optionItems(row.field);
      const items = withMissing(base.map((it) => ({ ...it, disabled: used.includes(decode(it.value)) })), row.value, row.field);
      return row.value == null ? [PICK, ...items] : items;
    };

    return (
      <>
        {many && (
          <div role="group" aria-label="조건 묶기" style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
            {[['and', '모두 만족'], ['or', '하나라도 만족']].map(([value, label]) => (
              <button key={value} type="button" aria-pressed={logic === value} disabled={readOnly}
                onClick={() => emit({ logic: value })}
                style={smallButton(logic === value ? 'accent' : 'secondary', readOnly, { flex: 1 })}>
                {label}
              </button>
            ))}
          </div>
        )}
        {rows.map((row, index) => {
          const n = index + 1;
          return (
            <div key={index} role="group" aria-label={`조건 ${n}`} style={rowBox}>
              <div style={rowHead}>
                <span style={note}>{`조건 ${n}`}</span>
                {many && (
                  <button type="button" aria-label={`조건 ${n} 지우기`} disabled={readOnly}
                    onClick={() => remove(index)} style={removeButton(readOnly)}>✕</button>
                )}
              </div>
              <select aria-label={`조건 ${n} 필드`} value={row.field || ''} disabled={readOnly}
                onChange={(e) => changeField(index, e.target.value)} style={control}>
                {FIELDS.map((f) => <option key={f} value={f}>{FIELD_LABELS[f]}</option>)}
              </select>
              <select aria-label={`조건 ${n} 연산자`} value={row.operator || ''} disabled={readOnly}
                onChange={(e) => changeOperator(index, e.target.value)} style={control}>
                {(row.field === 'assignee' ? ASSIGNEE_OPERATORS : OPERATORS)
                  .map((op) => <option key={op.value} value={op.value}>{op.label}</option>)}
              </select>
              {COMPARE.includes(row.operator) && pickSelect({
                ariaLabel: `조건 ${n} 값`,
                value: row.value,
                items: valueItems(row, index),
                onPick: (value) => update(index, { value }),
              })}
            </div>
          );
        })}
        <button type="button" onClick={add} disabled={readOnly || full}
          style={smallButton('secondary', readOnly || full)}>조건 추가</button>
      </>
    );
  };

  // ─── 필드 바꾸기 ────────────────────────────────────────
  const renderSetField = () => {
    const rows = Array.isArray(data.fields)
      ? data.fields.map((r) => (r && typeof r === 'object' ? r : {}))
      : [];
    // 그 필드의 첫 값 — 상태는 첫 상태(비울 수 없다) · 그 밖은 «없음»
    const firstValue = (field) => {
      if (field !== 'status') return null;
      const all = candidates('status');
      return all.length ? all[0] : null;
    };
    const freeField = FIELDS.find((f) => !rows.some((r) => r.field === f));
    const full = rows.length >= MAX_SET_FIELDS || !freeField;
    const update = (index, patch) => emit({ fields: rows.map((r, i) => (i === index ? { ...r, ...patch } : r)) });
    const replaceRow = (index, next) => emit({ fields: rows.map((r, i) => (i === index ? next : r)) });
    // 담당자 줄 방식 — add·remove 말고는(키 없음·모르는 값) 바꾸기다.
    const modeOf = (row) => (row.mode === 'add' || row.mode === 'remove' ? row.mode : 'replace');
    // 🔴 필드를 바꾸면 mode 키를 지운다 — update 의 {...r, ...patch} 는 옛 키를 남겨 상태 줄에 mode 가 따라간다.
    const changeField = (index, field) => {
      const next = { ...rows[index], field, value: firstValue(field) };
      delete next.mode;
      replaceRow(index, next);
    };
    // 바꾸기는 mode 키를 지운다(키 없음 = replace). 더하기·빼기는 «누구를» 이 있어야 해서(서버가 null 을 400 으로
    // 거절한다) 값이 비어 있으면 «트리거한 사람» 으로 채운다.
    const changeMode = (index, mode) => {
      const next = { ...rows[index] };
      if (mode === 'add' || mode === 'remove') {
        next.mode = mode;
        if (next.value == null) next.value = ACTOR;
      } else {
        delete next.mode;
      }
      replaceRow(index, next);
    };
    const add = () => {
      if (full) return;
      emit({ fields: [...rows, { field: freeField, value: firstValue(freeField) }] });
    };
    const remove = (index) => {
      if (rows.length < 2) return;
      emit({ fields: rows.filter((_, i) => i !== index) });
    };
    const valueItems = (row) => {
      // 더하기·빼기에는 «없음» 이 없다(비우기는 바꾸기만 된다).
      const assigneeAdds = row.field === 'assignee' && modeOf(row) !== 'replace';
      let items;
      if (row.field === 'status') items = optionItems('status');
      else if (row.field === 'assignee') items = assigneeAdds ? [ACTOR_ITEM, ...memberItems] : [NONE, ACTOR_ITEM, ...memberItems];
      else items = [NONE, ...optionItems(row.field)];
      items = withMissing(items, row.value, row.field);
      return (row.field === 'status' || assigneeAdds) && row.value == null ? [PICK, ...items] : items;
    };

    return (
      <>
        {rows.map((row, index) => {
          const n = index + 1;
          return (
            <div key={index} style={rowBox}>
              <div style={rowHead}>
                <span style={note}>{`필드 ${n}`}</span>
                {rows.length >= 2 && (
                  <button type="button" aria-label={`필드 ${n} 지우기`} disabled={readOnly}
                    onClick={() => remove(index)} style={removeButton(readOnly)}>✕</button>
                )}
              </div>
              <select aria-label={`필드 ${n}`} value={row.field || ''} disabled={readOnly}
                onChange={(e) => changeField(index, e.target.value)}
                style={control}>
                {FIELDS.map((f) => (
                  <option key={f} value={f} disabled={rows.some((r, i) => i !== index && r.field === f)}>
                    {FIELD_LABELS[f]}
                  </option>
                ))}
              </select>
              {row.field === 'assignee' && (
                <select aria-label={`필드 ${n} 방식`} value={modeOf(row)} disabled={readOnly}
                  onChange={(e) => changeMode(index, e.target.value)} style={control}>
                  {ASSIGNEE_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              )}
              {FIELDS.includes(row.field) && pickSelect({
                ariaLabel: `필드 ${n} 값`,
                value: row.value,
                items: valueItems(row),
                onPick: (value) => update(index, { value }),
              })}
            </div>
          );
        })}
        <button type="button" onClick={add} disabled={readOnly || full}
          style={smallButton('secondary', readOnly || full)}>필드 추가</button>
      </>
    );
  };

  // ─── 업무 만들기 ────────────────────────────────────────
  const renderCreateTask = () => {
    const blocked = SUBTASK_BLOCKED.includes(triggerType);
    const asSubtask = data.as_subtask === true;
    const pick = (key, items) => labeled(FIELD_LABELS[key], pickSelect({
      value: data[key],
      items: withMissing(items, data[key], key),
      onPick: (value) => emit({ [key]: value }),
    }));
    return (
      <>
        {labeled('제목', (
          <input ref={textRef} type="text" aria-label="새 업무 제목" maxLength={MAX_TITLE_LEN}
            value={typeof data.title === 'string' ? data.title : ''} disabled={readOnly}
            onChange={(e) => emit({ title: e.target.value })} style={control} />
        ))}
        {renderVariables('title')}
        {pick('status', [{ value: '', label: '첫 상태' }, ...optionItems('status')])}
        {pick('assignee', [NONE, ACTOR_ITEM, CURRENT_ITEM, ...memberItems])}
        {pick('type', [NONE, ...optionItems('type')])}
        {pick('priority', [NONE, ...optionItems('priority')])}
        {/* 🔴 삭제·보관 트리거에서는 켤 수 없다 — 이미 켜져 있으면(트리거를 나중에 바꾼 경우) 끄기만 허용한다. */}
        <label style={checkRow}>
          <input type="checkbox" checked={asSubtask} disabled={readOnly || (blocked && !asSubtask)}
            onChange={(e) => {
              if (e.target.checked && blocked) return;
              emit({ as_subtask: e.target.checked });
            }} />
          하위 업무로 만들기
        </label>
        {blocked && (
          <div style={{ fontSize: '11px', color: COLORS.textSub }}>이 트리거에서는 하위 업무를 만들 수 없습니다.</div>
        )}
      </>
    );
  };

  // ─── 메신저 채널에 글 ───────────────────────────────────
  const renderMessenger = () => {
    const value = typeof data.channel_id === 'string' ? data.channel_id : '';
    // 목록을 받았고 봇이 켜져 있을 때만 고른다 — 못 받음(null·읽기 전용)·봇 미설정이면 값과 무관하게 막는다.
    const listed = Array.isArray(channels) && channelsEnabled !== false;
    const list = listed ? channels.filter((c) => c && c.id != null) : [];
    const teams = [];   // 목록에 처음 나온 차례
    list.forEach((c) => {
      const team = c.team || '';
      if (!teams.includes(team)) teams.push(team);
    });
    const missing = listed && value !== '' && !list.some((c) => String(c.id) === value);
    let choices;
    if (listed) {
      choices = (
        <>
          <option value="">채널 고르기</option>
          {missing && <option value={value}>(없는 채널)</option>}
          {teams.map((team) => (
            <optgroup key={team} label={team}>
              {list.filter((c) => (c.team || '') === team).map((c) => {
                const name = c.name || String(c.id);
                return <option key={String(c.id)} value={String(c.id)}>{team ? `${team} · ${name}` : name}</option>;
              })}
            </optgroup>
          ))}
        </>
      );
    } else if (value) {
      choices = <option value={value}>(지정된 채널)</option>;
    } else {
      choices = <option value="">채널 고르기</option>;
    }
    return (
      <>
        {channelsEnabled === false && (
          <div style={{ fontSize: '12px', color: COLORS.warn, marginBottom: '8px' }}>
            메신저 봇이 설정되지 않아 채널을 고를 수 없습니다.
          </div>
        )}
        <label style={{ display: 'block', marginBottom: '4px' }}>
          <span style={labelText}>채널</span>
          <select aria-label="메신저 채널" value={value} disabled={readOnly || !listed}
            onChange={(e) => emit({ channel_id: e.target.value })} style={control}>
            {choices}
          </select>
        </label>
        <div style={{ ...note, marginBottom: '12px' }}>공개 채널 중 봇이 들어가 있는 채널만 보입니다.</div>
        {labeled('메시지', (
          <textarea ref={textRef} aria-label="메시지" rows={5} maxLength={MAX_MESSAGE_LEN}
            value={typeof data.message === 'string' ? data.message : ''} disabled={readOnly}
            onChange={(e) => emit({ message: e.target.value })}
            style={{ ...control, resize: 'vertical', fontFamily: 'inherit' }} />
        ))}
        {renderVariables('message')}
      </>
    );
  };

  const renderBody = () => {
    if (!spec) return <div style={note}>이 노드는 설정할 항목이 없습니다.</div>;
    switch (node.type) {
      case 'trigger_field_change': return renderFieldChange();
      case 'condition_if': return renderCondition();
      case 'action_set_field': return renderSetField();
      case 'action_create_task': return renderCreateTask();
      case 'action_messenger_post': return renderMessenger();
      default: return <div style={note}>따로 정할 것이 없습니다.</div>;
    }
  };

  return (
    <aside aria-label="노드 설정" style={{
      width: '260px', flex: '0 0 260px', boxSizing: 'border-box', height: '100%', padding: '12px',
      overflowY: 'auto', borderLeft: `1px solid ${COLORS.borderSoft}`, background: COLORS.surface,
      ...(node ? null : { display: 'flex', alignItems: 'center', justifyContent: 'center' }),
    }}>
      {node ? (
        <>
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: COLORS.text, wordBreak: 'break-all' }}>
              {spec ? spec.label : String(node.type)}
            </div>
            {group && <div style={{ fontSize: '12px', color: COLORS.textMuted, marginTop: '2px' }}>{group.label}</div>}
          </div>
          {renderBody()}
        </>
      ) : (
        <div style={{ fontSize: '13px', color: COLORS.textSub, textAlign: 'center' }}>노드를 고르면 여기서 설정합니다.</div>
      )}
    </aside>
  );
}
